import type { TFunction } from "i18next";
import type { EnterpriseOverviewFacility } from "../../api/enterpriseClient";
import type { BreakdownRow, FacilityStatus } from "./types";
import { SCORE_MAX } from "./types";

export const statusToneMap = {
  healthy: "positive",
  atRisk: "warning",
  critical: "negative",
} as const;

export function overallScoreClassName(status: FacilityStatus) {
  if (status === "healthy") return "text-text-success";
  if (status === "atRisk") return "text-warning";
  return "text-error";
}

export function buildBreakdownRows({
  facility,
  alertOutcomeScore,
  executionSpeedScore,
  t,
}: {
  facility: EnterpriseOverviewFacility;
  alertOutcomeScore: number | null;
  executionSpeedScore: number | null;
  t: TFunction;
}): BreakdownRow[] {
  const alertCount = getOpenAlertCount(facility);
  const mttrHours = facility.mttrHours;
  const responseHours =
    facility.meanTimeToRespondHours ?? facility.meanTimeToCommentHours;
  const hoursBetweenAlerts =
    facility.mtbfDays === null ? null : facility.mtbfDays * 24;

  return [
    {
      id: "alert-volume",
      label: t("enterprise.facilityDetail.drawer.alertVolumeLabel"),
      weight: 25,
      score:
        scoreFromPercent(alertOutcomeScore) ??
        scoreFromLowerIsBetter(alertCount, 6),
      tone: toneForScore(
        scoreFromPercent(alertOutcomeScore) ??
          scoreFromLowerIsBetter(alertCount, 6),
      ),
      current: t("enterprise.facilityDetail.drawer.alertsValue", {
        count: alertCount,
      }),
      target: t("enterprise.facilityDetail.drawer.alertVolumeTarget"),
      description: t("enterprise.facilityDetail.drawer.alertVolumeDescription"),
    },
    {
      id: "average-resolution-time",
      label: t("enterprise.facilityDetail.drawer.averageResolutionTimeLabel"),
      weight: 30,
      score: scoreFromLowerIsBetter(mttrHours, 12),
      tone: toneForScore(scoreFromLowerIsBetter(mttrHours, 12)),
      current: formatNullableHours(mttrHours, t),
      target: t("enterprise.facilityDetail.drawer.resolutionTarget"),
      description: t(
        "enterprise.facilityDetail.drawer.averageResolutionTimeDescription",
      ),
    },
    {
      id: "average-response-time",
      label: t("enterprise.facilityDetail.drawer.averageResponseTimeLabel"),
      weight: 25,
      score:
        scoreFromPercent(executionSpeedScore) ??
        scoreFromLowerIsBetter(responseHours, 2),
      tone: toneForScore(
        scoreFromPercent(executionSpeedScore) ??
          scoreFromLowerIsBetter(responseHours, 2),
      ),
      current: formatNullableHours(responseHours, t),
      target: t("enterprise.facilityDetail.drawer.responseTarget"),
      description: t(
        "enterprise.facilityDetail.drawer.averageResponseTimeDescription",
      ),
    },
    {
      id: "average-time-between-alerts",
      label: t(
        "enterprise.facilityDetail.drawer.averageTimeBetweenAlertsLabel",
      ),
      weight: 20,
      score: scoreFromHigherIsBetter(hoursBetweenAlerts, 40),
      tone: toneForScore(scoreFromHigherIsBetter(hoursBetweenAlerts, 40)),
      current: formatNullableHours(hoursBetweenAlerts, t),
      target: t("enterprise.facilityDetail.drawer.timeBetweenAlertsTarget"),
      description: t(
        "enterprise.facilityDetail.drawer.averageTimeBetweenAlertsDescription",
      ),
    },
  ];
}

export function getOpenAlertCount(facility: EnterpriseOverviewFacility) {
  return Math.max(
    0,
    facility.maintenanceRecommendedMachineCount + facility.watchingAlertsCount,
  );
}

export function getFacilityRegionLabel(facility: EnterpriseOverviewFacility) {
  if (facility.facilityGroupNames.length > 0) {
    return facility.facilityGroupNames[0];
  }
  if (facility.city && facility.state) return facility.state;
  return facility.state ?? facility.city ?? facility.facilityName;
}

export function formatScore(value: number) {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

function scoreFromPercent(value: number | null) {
  return value === null ? null : clamp((value / 100) * SCORE_MAX, 0, SCORE_MAX);
}

function scoreFromLowerIsBetter(value: number | null, target: number) {
  if (value === null) return null;
  if (value <= target) return SCORE_MAX;
  return clamp((target / Math.max(value, 0.1)) * SCORE_MAX, 0, SCORE_MAX);
}

function scoreFromHigherIsBetter(value: number | null, target: number) {
  if (value === null) return null;
  return clamp((value / target) * SCORE_MAX, 0, SCORE_MAX);
}

function toneForScore(score: number | null): BreakdownRow["tone"] {
  if (score === null) return "warning";
  if (score < 10) return "negative";
  if (score < 18) return "warning";
  return "positive";
}

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

function formatNullableHours(value: number | null, t: TFunction) {
  if (value === null) return t("enterprise.facilityDetail.drawer.unavailable");
  return t("enterprise.facilityDetail.drawer.hoursValue", {
    hours: value.toFixed(1),
  });
}
